import { useUserStore } from "@/stores/useUserStore.ts";
import {
  type ArrayResponse,
  authenticatedFetch,
  getApiUri,
  getFlowUri,
  QueryFromVars,
  throwBody,
} from "@/api/shared.ts";
import type { InterventionMetadata, Tutor, TutorDetails, TutorStatus } from "@/types/tutor.ts";
import { assert } from "@/helpers/console.ts";
import { JsonParse } from "@/helpers/json.ts";

export const postTutorStatus = async (status: TutorStatus): Promise<void> => {
  const { tutorId } = useUserStore.getState();

  if (tutorId === -1) {
    throw new Error("Invalid user id (-1 provided)");
  }

  const response = await authenticatedFetch(
    getApiUri(`/v4/admin/tutors/${tutorId}/status/${status}`),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      priority: "high",
      keepalive: true,
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return;
};

export const getTutors = async (): Promise<ArrayResponse<Tutor>> => {
  const response = await authenticatedFetch(getApiUri(`/v4/admin/tutors`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    priority: "low",
  });

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const putTutorCapacity = async (capacity: number): Promise<void> => {
  const { userId } = useUserStore.getState();
  const response = await authenticatedFetch(getApiUri(`/v3/users/${userId}/tutors`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    priority: "high",
    body: JSON.stringify({
      capacity: capacity,
      isAutoInterventionsEnabled: true,
    }),
  });

  if (!response.ok) {
    throw await throwBody(response);
  }
};

// TODO: Move if used elsewhere
interface TutorIntervention {
  flowGeneratorSessionId: number;
  flowGeneratorSessionInterventionId: number;
  tutorId: number;
  dateCreated: Date | null;
  dateTutorJoined: Date | null;
  dateEnded: Date | null;
  socratesSessionId: string | null;
  metadata: string | InterventionMetadata;
  lessonType: string;
  lessonName: string;
  topicPathwayQuizId: number;
  tutorFirstName: string;
  tutorLastName: string;
  studentUserId: number;
  studentFirstName: string;
  studentLastName: string;
}

export const getTutorInterventions = async () => {
  const { tutorId } = useUserStore.getState();
  return getTutorInterventionsTutorId(tutorId, false);
};

export const getTutorInterventionsTutorId = async (
  tutorId: number,
  findOldSessions?: boolean,
): Promise<Array<TutorIntervention>> => {
  if (tutorId === -1) {
    throw new Error("Invalid user id (-1 provided)");
  }

  let maxLoopCount = 1;
  const data = [];

  for (let i = 0; i < maxLoopCount; i++) {
    const extra = QueryFromVars([
      {
        name: "skip",
        value: i === 0 ? undefined : 10 * i,
      },
      {
        name: "take",
        value: i === 0 ? undefined : 10,
      },
    ]);

    // oxlint-disable-next-line no-await-in-loop
    const response = await authenticatedFetch(
      getApiUri(`/v4/admin/tutors/${tutorId}/interventions${extra}`),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        priority: "low",
      },
    );

    if (!response.ok) {
      // oxlint-disable-next-line no-await-in-loop
      throw await throwBody(response);
    }

    // oxfmt-ignore
    const newItems = (
      // oxlint-disable-next-line no-await-in-loop
      await response.json().then((data: ArrayResponse<TutorIntervention>) => {
        return {
          data: data.data.map((item: TutorIntervention) => {
            return {
              ...item,
              metadata: JsonParse<InterventionMetadata>(item.metadata as string) ?? "",
            };
          }),
          total: data.total,
        };
      })
    ).data;

    data.push(...newItems);
    if (findOldSessions && newItems.length > 0) {
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const hasOlderNewTutorJoinedDate = newItems.some((item) => {
        if (!item.dateTutorJoined) return false;
        return new Date(item.dateTutorJoined).getTime() < oneDayAgo;
      });

      if (!hasOlderNewTutorJoinedDate) {
        maxLoopCount += 1;
      }
    }
  }

  return data;
};

export const getTutor = async (tutorId: number): Promise<TutorDetails> => {
  const response = await authenticatedFetch(getApiUri(`/v4/tutors/${tutorId}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    priority: "high",
  });

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const putTutorSocrateRating = async (
  tutorId: number,
  interventionId: number,
  rating: number,
  wow: boolean,
  positive?: string,
  negative?: string,
  accuracy?: number,
) => {
  assert(rating >= 0 && rating <= 3, "Rating must be between 0 and 3", rating);

  if (accuracy)
    assert(accuracy >= 1 && accuracy <= 5, "Accuracy must be between 1 and 5", accuracy);

  const response = await authenticatedFetch(
    getApiUri(
      `/v3/tutors/${tutorId}/flow-generator-session-interventions/${interventionId}/feedback`,
    ),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      priority: "high",
      body: JSON.stringify({
        socratePedagogyAccuracy: accuracy,
        tutorSatisfactionRating: rating,
        interventionPositiveFeedback: positive,
        interventionNegativeFeedback: negative,
        isInterventionWowMoment: wow,
      }),
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return;
};

interface TutorSocratesRatingResponse {
  tutorSatisfactionRating: number | null;
  interventionPositiveFeedback?: string | null;
  interventionNegativeFeedback?: string | null;
  isInterventionWowMoment: boolean | null;
  socratePedagogyAccuracy?: string | null;
}

export const getTutorSocrateRating = async (
  tutorId: number,
  interventionId: number,
): Promise<TutorSocratesRatingResponse> => {
  const response = await authenticatedFetch(
    getApiUri(
      `/v3/tutors/${tutorId}/flow-generator-session-interventions/${interventionId}/feedback`,
    ),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      priority: "high",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }

  return response.json();
};

export const postTutorTyping = async (flowSessionId: number) => {
  const { userId } = useUserStore.getState();
  const response = await authenticatedFetch(
    getFlowUri(`/v1/users/${userId}/flow-generator-sessions/${flowSessionId}/typing`),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      priority: "low",
    },
  );

  if (!response.ok) {
    throw await throwBody(response);
  }
};
